import { DATE_FORMATS } from '@web-platform/shared-universal-constants/src/dateFormats';
import { RADAR_MAX_DESTINATIONS } from '@web-platform/shared-universal-constants/src/radar';
import { TARGET_GEO_LEVEL } from '@web-platform/shared-universal-constants/src/targetGeoLevel';
import { CONFIG_SERVICE_KEYS } from '@web-platform/shared-universal-constants-config';
import {
  DATE_TYPE,
  FLEXIBLE_TYPE,
  LOCATION_TYPE,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarUnfocusedSearch';
import {
  RADAR_CREATE_SESSIONS,
  SONAR_V3_CREATE_SESSION,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { getConfigServiceValue } from '@web-platform/shared-universal-utils-frontend/src/config';
import { getEntityId } from '@web-platform/shared-universal-utils-frontend/src/helpers/getEntityId';
import { getTravellerContextId } from '@web-platform/shared-universal-utils-frontend/src/helpers/getTravellerContextId';
import {
  formatDateNonlocalised,
  getCulture,
  getYearMonthDay,
  isMonthDate,
} from '@web-platform/shared-universal-utils-frontend-localisation';
import { isBefore } from 'date-fns/isBefore';
import { startOfMonth } from 'date-fns/startOfMonth';
import { createSelector } from 'reselect';

import { isValidSearchForHotels } from '../helpers/isValidSearchForHotels';
import {
  getSession,
  getTrustedFunnel,
  getUserDevice,
  getUserIdentifiers,
  getUserSearch,
} from '../state';

import { getResultsView, isCityCountrySwitchEnabled, RESULTS_VIEW } from './resultsView';
import { getCabinClass, getSearchLegs } from './search';

import type { NormalizedSearchLeg } from './search';
import type { CabinClass } from '@web-platform/shared-universal-constants/src/cabinClass';
import type { DateString } from '@web-platform/shared-universal-types/src/dateString';
import type { AlternativeDatesVariables } from '@web-platform/shared-universal-types/src/network/acorn/requestTypes/alternativeDates';
import type {
  FlexibleDates,
  FlexibleLocation,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarUnfocusedSearch';
import type {
  FlightsPricingBookingMutationVariables,
  FlightsPricingBookingMutationVariablesLeg,
} from '@web-platform/shared-universal-types/src/network/requestTypes/flightsPricingBookingRequestTypes';
import type {
  RadarMutationVariables,
  RadarMutationVariablesLeg,
} from '@web-platform/shared-universal-types/src/network/requestTypes/radarRequestTypes';
import type { SearchLegPlace } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';
import type { StateType } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';
import type { TargetGeoLevelType } from '@web-platform/shared-universal-types/src/requestContext';
import type { EntityId, Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

type Props = {
  itineraryId?: string;
  isTokenURL: boolean;
};

// --- Pure utility functions (from Banana) ---

export const getUserSearchLegs = (
  normalizedLegs: NormalizedSearchLeg[],
  alternativeOrigins?: boolean,
  alternativeDestinations?: boolean,
): FlightsPricingBookingMutationVariablesLeg[] => {
  if (!normalizedLegs.length) {
    return [];
  }

  const outbound = normalizedLegs[0];
  const returnLeg =
    normalizedLegs.length > 1 ? normalizedLegs[normalizedLegs.length - 1] : undefined;

  return [
    {
      date: outbound.date,
      returnDate: returnLeg?.date,
      addAlternativeOrigins: !!alternativeOrigins,
      addAlternativeDestinations: !!alternativeDestinations,
      originEntityId: outbound.origin?.travelEntityId,
      destinationEntityId: outbound.destination?.travelEntityId,
    },
  ];
};

const getDate = (dateStr: DateString): FlexibleDates => {
  if (!dateStr) {
    return { [FLEXIBLE_TYPE]: DATE_TYPE.ANYTIME };
  }

  const isDateWholeMonth = isMonthDate(dateStr);

  const isInvalidDate = isDateWholeMonth
    ? isBefore(dateStr, startOfMonth(Date.now()))
    : isBefore(dateStr, Date.now());

  if (isInvalidDate) {
    const currentDateStr = formatDateNonlocalised(new Date(), DATE_FORMATS.YEAR_MONTH_DAY_DASHED);

    const { day: todayDay, month: todayMonth, year: todayYear } = getYearMonthDay(currentDateStr);

    return isDateWholeMonth
      ? {
          [FLEXIBLE_TYPE]: DATE_TYPE.MONTH,
          year: todayYear,
          month: todayMonth,
        }
      : {
          [FLEXIBLE_TYPE]: DATE_TYPE.DATE,
          year: todayYear,
          month: todayMonth,
          day: todayDay,
        };
  }

  const { day, month, year } = getYearMonthDay(dateStr);

  return isDateWholeMonth
    ? {
        [FLEXIBLE_TYPE]: DATE_TYPE.MONTH,
        year,
        month,
      }
    : {
        [FLEXIBLE_TYPE]: DATE_TYPE.DATE,
        year,
        month,
        day,
      };
};

const getLeg = (searchPlace: SearchLegPlace): FlexibleLocation => {
  const entityId = getEntityId(searchPlace);
  return entityId
    ? {
        [FLEXIBLE_TYPE]: LOCATION_TYPE.ENTITY,
        entityId,
      }
    : {
        [FLEXIBLE_TYPE]: LOCATION_TYPE.EVERYWHERE,
      };
};

const getSearchLeg = (
  origin: SearchLegPlace,
  destination: SearchLegPlace,
  date: DateString,
  placeOfStay?: Nullable<EntityId>,
) => ({
  legOrigin: getLeg(origin),
  legDestination: getLeg(destination),
  dates: getDate(date),
  placeOfStay,
});

export const getRadarUserSearchLegs = (
  normalizedLegs: NormalizedSearchLeg[],
): RadarMutationVariablesLeg[] => {
  if (!normalizedLegs.length) {
    return [];
  }

  return normalizedLegs.map((leg) =>
    getSearchLeg(leg.origin, leg.destination, leg.date, leg.placeOfStay),
  );
};

// --- Banana selector factories ---

export const makeGetFlightsPricingBookingVariables = () =>
  createSelector(
    [
      getUserSearch,
      getUserDevice,
      getUserIdentifiers,
      getTrustedFunnel,
      (state: StateType) => getSession(state, RADAR_CREATE_SESSIONS),
      (state: StateType) => getSession(state, SONAR_V3_CREATE_SESSION),
      (_: StateType, props: Props) => props && props.itineraryId,
      (_: StateType, props: Props) => props && props.isTokenURL,
      getSearchLegs,
    ],
    (
      userSearch,
      userDevice,
      userIdentifiers,
      trustedFunnel,
      radarSession,
      bookingPanelSession,
      itineraryId,
      isTokenURL,
      normalizedLegs,
    ): FlightsPricingBookingMutationVariables => {
      const { isMobilePhone, isTablet } = userDevice;
      const { alternativeDestinations, alternativeOrigins, ...userSearchRest } = userSearch;

      return {
        ...userSearchRest,
        legs: getUserSearchLegs(normalizedLegs, alternativeOrigins, alternativeDestinations),
        cabinClass: userSearch.cabinClass,
        cabin_class: userSearch.cabinClass,
        // Both cabin_class and cabinClass are required in payload
        ...getCulture(),
        isMobilePhone,
        isTablet,
        // If there is an existing session, we'll use the latest viewGuid
        // if there is no session, we'll want the variables to contain the latest funnel
        // viewGuid to create a new session
        viewId: radarSession.sessionViewGuid || trustedFunnel.viewGuid,
        travellerContextId: getTravellerContextId(userIdentifiers),
        utid: userIdentifiers.utid,
        flightsPricingSessionId:
          bookingPanelSession.flightsSessionId ?? radarSession.flightsSessionId,
        sessionViewGuid: radarSession.sessionViewGuid,
        itineraryId: isTokenURL ? undefined : itineraryId,
        itineraryToken: isTokenURL ? itineraryId : undefined,
      };
    },
  );

export const makeGetRadarMutationVariables = () =>
  createSelector(
    [
      getUserSearch,
      getUserDevice,
      getUserIdentifiers,
      getTrustedFunnel,
      (state: StateType) => getSession(state, RADAR_CREATE_SESSIONS),
      (state: StateType) => isValidSearchForHotels(state),
      getSearchLegs,
      getResultsView,
    ],
    (
      userSearch,
      userDevice,
      userIdentifiers,
      trustedFunnel,
      session,
      isValidHotelSearch,
      normalizedLegs,
      resultsView,
    ): RadarMutationVariables => {
      const { isMobilePhone, isTablet } = userDevice;

      const { cabinClass, fareAttributes, sponsoredAgent, ...userSearchRest } = userSearch;

      const legs = getRadarUserSearchLegs(normalizedLegs);

      const { currency, locale, market } = getCulture();

      const cityCountrySwitchVariant = getConfigServiceValue(
        CONFIG_SERVICE_KEYS.EXPERIMENT.EXPLORE_CITY_COUNTRY_POC_VARIANT,
      );
      const enableCityCountryPoc = isCityCountrySwitchEnabled(cityCountrySwitchVariant);

      const targetGeoLevel: TargetGeoLevelType | undefined =
        sponsoredAgent || (enableCityCountryPoc && resultsView === RESULTS_VIEW.CITIES)
          ? TARGET_GEO_LEVEL.CITY
          : undefined;
      const brandAirlineId: string | undefined = sponsoredAgent ?? undefined;

      return {
        ...userSearchRest,
        legs,
        cabinClass,
        cabin_class: cabinClass,
        // Both cabin_class and cabinClass are required in payload
        currency,
        locale,
        market,
        isMobilePhone,
        isTablet,
        // If there is an existing session, we'll use the latest viewGuid
        // if there is no session, we'll want the variables to contain the latest funnel
        // viewGuid to create a new session
        viewId: session.sessionViewGuid || trustedFunnel.viewGuid,
        travellerContextId: getTravellerContextId(userIdentifiers),
        utid: userIdentifiers.utid,
        isValidHotelSearch,
        fareAttributes,
        maxDestinations: RADAR_MAX_DESTINATIONS,
        ...(targetGeoLevel ? { targetGeoLevel } : {}),
        ...(brandAirlineId ? { brandAirlineId } : {}),
      };
    },
  );

export const makeGetPremiumRadarMutationVariables = () =>
  createSelector(
    [
      getUserSearch,
      getUserDevice,
      getUserIdentifiers,
      getTrustedFunnel,
      getSearchLegs,
      (_state: StateType, cabinClass: CabinClass) => cabinClass,
    ],
    (
      userSearch,
      userDevice,
      userIdentifiers,
      trustedFunnel,
      normalizedLegs,
      cabinClass,
    ): RadarMutationVariables => {
      const { isMobilePhone, isTablet } = userDevice;

      const {
        cabinClass: _userSearchCabinClass,
        fareAttributes,
        sponsoredAgent: _sponsoredAgent,
        ...userSearchRest
      } = userSearch;

      const legs = getRadarUserSearchLegs(normalizedLegs);

      const { currency, locale, market } = getCulture();

      return {
        ...userSearchRest,
        legs,
        cabinClass,
        cabin_class: cabinClass,
        currency,
        locale,
        market,
        isMobilePhone,
        isTablet,
        // No session fallback: premium explore is fire-once (no polling), so there's no
        // session to pin to. Reading sessionViewGuid would also couple this to economy state.
        viewId: trustedFunnel.viewGuid,
        travellerContextId: getTravellerContextId(userIdentifiers),
        utid: userIdentifiers.utid,
        isValidHotelSearch: false,
        fareAttributes,
        targetGeoLevel: TARGET_GEO_LEVEL.CITY,
      };
    },
  );

// --- Acorn selector factories ---

// TODO: LUNA-3866: Migrate to normalizing selectors (getOutboundDate, getInboundDate, getDestination)
// once userSearch leg dates are aligned with top-level dates in the SSR response.
// Currently reads stale-on-Banana top-level fields directly — safe on Acorn only.
export const makeGetAcornRadarMutationVariables = () =>
  createSelector(
    [getUserSearch, getUserDevice, getUserIdentifiers],
    (userSearch, userDevice, userIdentifiers) => {
      const { currency, locale, market } = getCulture();
      const { isMobilePhone, isTablet } = userDevice;
      const { utid, viewId } = userIdentifiers;
      const {
        adults,
        alternativeDestinations,
        alternativeOrigins,
        cabinClass,
        child_ages: childAges,
        fareAttributes,
        inboundDate,
        legs,
        options,
        outboundDate,
      } = userSearch;

      return {
        currency,
        locale,
        market,
        alternativeDestinations,
        alternativeOrigins,
        adults,
        cabinClass,
        childAges,
        inboundDate,
        legs,
        options,
        outboundDate,
        isMobilePhone,
        isTablet,
        travellerContextId: utid,
        destinationGeoContainerId: legs?.[0]?.destination?.geoContainerId,
        viewId,
        utid,
        fareAttributes,
      };
    },
  );

export const makeGetAlternativeDatesSearchVariables = () =>
  createSelector(
    [getUserSearch, getUserIdentifiers, getCabinClass, getSearchLegs],
    (userSearch, userIdentifiers, cabinClass, normalizedLegs): AlternativeDatesVariables => {
      const { adults, alternativeDestinations, alternativeOrigins, child_ages } = userSearch;
      const { currency, locale, market } = getCulture();
      const { viewId } = userIdentifiers;

      const legs = normalizedLegs.map((leg) => ({
        date: getYearMonthDay(leg.date),
        origin: [leg.origin.entityId ?? ''],
        destination: [leg.destination.entityId ?? ''],
      }));

      return {
        adults,
        childAges: child_ages,
        cabinClass,
        legs,
        nearbyAirports: {
          includeOriginNearbyAirports: alternativeDestinations,
          includeDestinationNearbyAirports: alternativeOrigins,
        },
        locale,
        market,
        currency,
        viewId,
      };
    },
  );
